import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  User,
  MapPin,
  Phone,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MoreHorizontal,
  Eye,
  Edit3
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Booking } from '@/types';
import { formatNZD } from '@/utils/currency';

const statusColorMap = {
  confirmed: {
    color: 'text-success-700 bg-success-50 border-success-200',
    icon: CheckCircle,
  },
  pending: {
    color: 'text-warning-700 bg-warning-50 border-warning-200',
    icon: AlertTriangle,
  },
  expired: {
    color: 'text-gray-700 bg-gray-50 border-gray-200',
    icon: AlertTriangle,
  },
  cancelled: {
    color: 'text-red-700 bg-red-50 border-red-200',
    icon: XCircle,
  },
  completed: {
    color: 'text-primary-700 bg-primary-50 border-primary-200',
    icon: CheckCircle,
  },
} as const;

interface TodayBookingsProps {
  bookings?: Booking[];
  loading?: boolean;
}

const TodayBookings: React.FC<TodayBookingsProps> = ({ bookings = [], loading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedBooking, setExpandedBooking] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="card card-comfortable">
        <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-4 border border-gray-200 rounded-xl animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="h-4 w-20 bg-gray-200 rounded"></div>
                <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
              </div>
              <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 w-24 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getStatusLabel = (status: Booking['status']) => {
    switch (status) {
      case 'confirmed':
        return t('bookings.confirmed');
      case 'pending':
        return t('bookings.pending');
      case 'cancelled':
        return t('bookings.cancelled');
      case 'completed':
        return t('bookings.completed');
      case 'expired':
        return t('bookings.expired');
      default:
        return status;
    }
  };

  return (
    <div className="card card-comfortable">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.todayBookings')}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('dashboard.todayBookingsCount', { count: bookings.length })}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-success-500"></div>
              <span className="text-gray-600">{t('bookings.confirmed')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-warning-500"></div>
              <span className="text-gray-600">{t('bookings.pending')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {bookings.map((booking, index) => {
            const bookingId = String(booking.id);
            const status = statusColorMap[booking.status];
            const isExpanded = expandedBooking === bookingId;
            const displayName =
              booking.customer_name ||
              booking.customer_display_name ||
              booking.customer_email ||
              booking.customer_email_reg ||
              t('common.unknownUser');
            const displayPhone = booking.customer_phone || booking.customer_phone_reg || '';
            const timeRange = `${booking.start_time}-${booking.end_time}`;

            return (
              <motion.div
                key={booking.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={clsx(
                  "border rounded-xl transition-all duration-200 hover:shadow-medium",
                  booking.status === 'cancelled' ? 'opacity-60' : '',
                  "border-gray-200 bg-white"
                )}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{timeRange}</span>
                      <span className={clsx(
                        "px-2 py-1 rounded-full text-xs font-medium border",
                        status?.color
                      )}>
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {formatNZD(booking.total_price)}
                      </span>
                      
                      <button
                        onClick={() => setExpandedBooking(
                          isExpanded ? null : bookingId
                        )}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{displayName}</span>
                      {booking.booking_type === 'walk_in' && (
                        <span className="px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700">
                          {t('bookings.walkIn')}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{booking.court_name}</span>
                    </div>
                  </div>

                  {displayPhone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <Phone className="h-4 w-4" />
                      <span>{displayPhone}</span>
                    </div>
                  )}

                  {booking.special_requests && (
                    <div className="p-3 bg-primary-50 rounded-lg mb-3">
                      <p className="text-sm text-primary-800">
                        <span className="font-medium">{t('createBooking.specialRequests')}:</span>
                        {booking.special_requests}
                      </p>
                    </div>
                  )}

                  {/* 展开操作 */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-gray-100 pt-3 mt-3"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/bookings/${booking.id}`)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                            {t('common.viewDetails')}
                          </button>
                          
                          <button
                            onClick={() => navigate(`/bookings/${booking.id}?mode=edit`)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit3 className="h-4 w-4" />
                            {t('common.edit')}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {bookings.length === 0 && (
        <div className="text-center py-12">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('bookings.noRecordsFound')}
          </h3>
          <p className="text-gray-500 mb-6">{t('bookings.tryAdjustFilter')}</p>
          <button className="btn btn-primary" onClick={() => navigate('/bookings/create')}>
            {t('bookings.createWalkIn')}
          </button>
        </div>
      )}
    </div>
  );
};

export default TodayBookings;
